import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { buildWhatsAppLink } from './whatsapp-fab';

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const BORDER = 'rgba(255,255,255,0.09)';
const SURFACE = 'rgba(27,16,41,0.85)';

const schema = z.object({
  name: z.string().min(2, 'Informe seu nome completo'),
  whatsapp: z.string().min(10, 'Informe um WhatsApp válido'),
  email: z.string().email('Informe um e-mail válido'),
  context: z.string().max(2000).optional(),
  // Honeypot anti-bot: campo oculto; humanos deixam vazio.
  website: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

type FormState = 'idle' | 'loading' | 'success' | 'error';

export function LandingLeadForm() {
  const [searchParams] = useSearchParams();
  const [formState, setFormState] = useState<FormState>('idle');
  const [lastValues, setLastValues] = useState<FormValues | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(values: FormValues) {
    setFormState('loading');
    setLastValues(values);

    const payload = {
      name: values.name,
      email: values.email,
      whatsapp: values.whatsapp,
      context: values.context?.trim() || undefined,
      website: values.website || undefined,
      source: 'landing' as const,
      utmSource: searchParams.get('utm_source') ?? undefined,
      utmMedium: searchParams.get('utm_medium') ?? undefined,
      utmCampaign: searchParams.get('utm_campaign') ?? undefined,
    };

    try {
      await apiClient.createLead(payload);
      setFormState('success');
      // Abre WhatsApp em nova aba
      window.open(buildWhatsAppLink(values.name), '_blank', 'noopener');
    } catch {
      setFormState('error');
    }
  }

  if (formState === 'success') {
    return <SuccessCard name={lastValues?.name} />;
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 520,
        background: SURFACE,
        border: `1px solid rgba(232,185,106,0.2)`,
        borderRadius: 18,
        padding: 'clamp(28px, 5vw, 44px)',
        backdropFilter: 'blur(24px)',
        boxShadow:
          '0 0 80px rgba(109,40,217,0.18), 0 2px 48px rgba(0,0,0,0.4)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow atrás do card */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 380,
          height: 260,
          background:
            'radial-gradient(ellipse at center, rgba(109,40,217,0.22) 0%, transparent 70%)',
          pointerEvents: 'none',
          filter: 'blur(2px)',
        }}
      />

      <div style={{ position: 'relative' }}>
        <h2
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 400,
            fontSize: 'clamp(22px, 3.5vw, 28px)',
            letterSpacing: '-0.01em',
            color: FG,
            marginBottom: 6,
          }}
        >
          Agendar demonstração
        </h2>
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 14,
            color: MUTED,
            marginBottom: 28,
          }}
        >
          Retornamos rápido pra marcar sua conversa. Sem compromisso.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Honeypot anti-bot: invisível para humanos; bots tendem a preencher */}
          <input
            {...register('website')}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              width: 1,
              height: 1,
              opacity: 0,
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Seu nome *" error={errors.name?.message}>
              <input
                {...register('name')}
                placeholder="Maria da Silva"
                style={inputStyle}
              />
            </Field>

            <Field label="WhatsApp com DDD *" error={errors.whatsapp?.message}>
              <input
                {...register('whatsapp')}
                placeholder="(51) 99999-9999"
                inputMode="tel"
                style={inputStyle}
              />
            </Field>

            <Field label="E-mail *" error={errors.email?.message}>
              <input
                {...register('email')}
                placeholder="maria@escritorio.com.br"
                type="email"
                style={inputStyle}
              />
            </Field>

            <Field label="Contexto" error={errors.context?.message}>
              <textarea
                {...register('context')}
                placeholder="Conte brevemente como é seu atendimento hoje..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              />
            </Field>

            {formState === 'error' && (
              <div
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  fontSize: 13,
                  color: '#FCA5A5',
                }}
              >
                Erro ao enviar. Fale com a gente diretamente pelo WhatsApp.
                <br />
                <a
                  href={buildWhatsAppLink(lastValues?.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#4ADE80',
                    fontWeight: 600,
                    textDecoration: 'none',
                    marginTop: 6,
                    display: 'inline-block',
                  }}
                >
                  Abrir WhatsApp
                </a>
              </div>
            )}

            <button
              type="submit"
              disabled={formState === 'loading'}
              style={{
                marginTop: 4,
                background:
                  formState === 'loading'
                    ? 'rgba(232,185,106,0.4)'
                    : `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${CHAMPAGNE_LIGHT} 100%)`,
                color: '#1A0F00',
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: '0.01em',
                border: 'none',
                borderRadius: 10,
                padding: '15px',
                cursor: formState === 'loading' ? 'not-allowed' : 'pointer',
                width: '100%',
                boxShadow: `0 4px 24px rgba(232,185,106,0.3)`,
                transition: 'opacity 0.15s ease, transform 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (formState !== 'loading') {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform =
                  'translateY(0)';
              }}
            >
              {formState === 'loading' ? 'Enviando...' : 'Agendar demonstração'}
            </button>
          </div>
        </form>

        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 12,
            color: 'rgba(169,159,184,0.55)',
            marginTop: 16,
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Ao enviar, você concorda com o uso dos seus dados para contato
          comercial. Seus dados não serão vendidos nem compartilhados com
          terceiros.
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontSize: 13,
          fontWeight: 500,
          color: MUTED,
          marginBottom: 6,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </label>
      {children}
      {error && (
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 12,
            color: '#FCA5A5',
            marginTop: 4,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function SuccessCard({ name }: { name?: string }) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 520,
        background: SURFACE,
        border: `1px solid rgba(74,222,128,0.22)`,
        borderRadius: 18,
        padding: 'clamp(36px, 6vw, 52px)',
        backdropFilter: 'blur(24px)',
        boxShadow: '0 0 80px rgba(74,222,128,0.1), 0 2px 48px rgba(0,0,0,0.4)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(74,222,128,0.12)',
          border: '1px solid rgba(74,222,128,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 24,
        }}
      >
        ✓
      </div>
      <h2
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 400,
          fontSize: 26,
          color: FG,
          marginBottom: 10,
        }}
      >
        Recebemos seu contato{name ? `, ${name.split(' ')[0]}` : ''}!
      </h2>
      <p
        style={{
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontSize: 15,
          color: MUTED,
          lineHeight: 1.6,
          marginBottom: 28,
        }}
      >
        Entraremos em contato pra agendar sua demonstração, geralmente em até
        48h.
      </p>
      <a
        href={buildWhatsAppLink(name)}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          background: 'rgba(37,211,102,0.12)',
          color: '#4ADE80',
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: 14,
          border: '1px solid rgba(37,211,102,0.25)',
          borderRadius: 9,
          padding: '12px 22px',
          textDecoration: 'none',
          transition: 'background 0.15s ease',
        }}
      >
        Falar pelo WhatsApp agora
      </a>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(10,7,16,0.6)',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: '11px 14px',
  fontFamily: "'Hanken Grotesk', sans-serif",
  fontSize: 14.5,
  color: FG,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};
